import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { DiveLog, DiveLogMapItem, PaginatedDiveLogs } from '@/types/api';

interface DiveLogListParams {
  page?: number;
  limit?: number;
}

export function useDiveLogs(params: DiveLogListParams = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 50;
  return useQuery({
    queryKey: ['dive-logs', { page, limit }],
    queryFn: () =>
      api<PaginatedDiveLogs>(`/api/v1/dive-logs?page=${page}&limit=${limit}`),
    placeholderData: keepPreviousData,
  });
}

export function useDiveLogsMap() {
  return useQuery({
    queryKey: ['dive-logs', 'map'],
    queryFn: () => api<{ items: DiveLogMapItem[] }>('/api/v1/dive-logs/map'),
    staleTime: 60_000,
  });
}

export function useDiveLog(id: number | undefined) {
  return useQuery({
    queryKey: ['dive-log', id],
    queryFn: () => api<DiveLog>(`/api/v1/dive-logs/${id}`),
    enabled: id !== undefined,
  });
}

export interface DiveLogInput {
  title: string;
  description?: string | null;
  diveDate: string;
  diveSiteId: number;
  diveTimeMinutes: number;
  maxDepth: number;
  waterTemperature?: number | null;
  type?: string | null;
  visibility?: string | null;
  current?: string | null;
  wetSuitType?: string | null;
  usedWeight?: number | null;
  airType?: string | null;
  nitroxO2Percentage?: number | null;
  initialPressure?: number | null;
  finalPressure?: number | null;
  rating?: number | null;
  buddies?: string | null;
}

export function useCreateDiveLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DiveLogInput) =>
      api<DiveLog>('/api/v1/dive-logs', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dive-logs'] }),
  });
}

export function useUpdateDiveLog(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<DiveLogInput>) =>
      api<DiveLog>(`/api/v1/dive-logs/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dive-logs'] });
      qc.invalidateQueries({ queryKey: ['dive-log', id] });
    },
  });
}

export function useDeleteDiveLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/v1/dive-logs/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dive-logs'] }),
  });
}
